import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useGridSettings, useRentals } from '@kaltura-ott/tvpil-shared';
import { RentalAsset } from '@kaltura-ott/tvpil-shared/lib/rhino/data/rental/types';

import { COMPONENT_NAME, PAGE_SIZE } from 'components/pages/Grids/constants';
import { useGridMatchPath } from 'components/pages/Grids/hooks/useGridMatchPath/useGridMatchPath';
import { DEFAULT_IMAGE_PARAMS } from 'consts';
import { WidgetProviderData } from 'context/widgetProvider/types';
import { useLoadingPerformanceEvents } from 'hooks';
import { useRhinoErrorReaction } from 'hooks/common/useRhinoErrorReaction';
import routes from 'routesPaths';
import { APPLICATION_SPINNER_EVENT_NAME, applyStyleHexToHSL, applyStyles } from 'utils';

function useRentalsGrid() {
  const { type, id } = useGridMatchPath(routes.rentalsGrid.path);
  const [, setForceUpdate] = useState(false);
  const settings = useGridSettings({
    gridReferenceId: type,
    railReferenceId: id,
    ...DEFAULT_IMAGE_PARAMS
  });
  const { items, totalCount, isLoading, hasNextPage, fetchNextPage, error, refetch } = useRentals({
    referenceId: id,
    pageSize: PAGE_SIZE,
  });
  const data: WidgetProviderData<RentalAsset> = {
    items,
    totalCount,
    hasNextPage,
    isLoading,
    error,
    fetchNextPage,
    refetch,
  };
  const wrapperRef = useRef(null);
  const shouldSendAppLoadingPerformanceEvent = isLoading && !data.items.length;
  const intl = useIntl();
  const notValidPageTitle = intl.formatMessage({
    id: `${COMPONENT_NAME}.notValidPage`,
    defaultMessage: 'Not a valid page',
  });

  useEffect(() => {
    function initGridPage() {
      if (!settings) {
        return;
      }

      const node = wrapperRef?.current;

      if (node && settings.branding) {
        applyStyles(node, settings.branding, false, true);
        applyStyleHexToHSL(node, settings.branding.backgroundColor);
      }
    }

    initGridPage();
  }, [type, id, settings]);

  useRhinoErrorReaction({ error, refetch });
  useLoadingPerformanceEvents(APPLICATION_SPINNER_EVENT_NAME.GRID_PAGE, shouldSendAppLoadingPerformanceEvent);

  return {
    data,
    settings,
    wrapperRef,
    setForceUpdate,
    notValidPageTitle,
  };
}
export { useRentalsGrid };
